// TODO: (important) Finalize this file and remove eslint-disable after approval has been confirmed. Extract any remaining text from the file and add it to the appropriate i18n file or backend (?).

import React, { useEffect, useMemo, useState } from 'react';
import i18n from 'i18n-react';
import { HashLink as Link } from 'react-router-hash-link';

import { ContentHeader } from 'components/ContentHeader/ContentHeader';
import RewardMenu from 'components/RewardMenu';
import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import StorageHandler from 'config/StorageHandler';
import { storageKeys } from 'config/settings.json';
import getFeatureFlags from 'helpers/getFeatureFlags';
import { isRewardsCountry } from 'helpers/isRewardsCountry';
import { legacyRemCss } from 'helpers/legacyRem';
import { useUserInfo } from 'hooks/useUserInfo';

const trackShare = () => {
  DataHandler.matchAppId().then((appId) => {
    StorageHandler.load(storageKeys.user).then((userObj) => {
      let email = '';
      ({ email } = userObj);
      APIHandler.trackEvent(appId, email, 'collateral', 'rewards', `${window.location.href}/main/rewards`, 'rewards');
    });
  });
};

const contentClickHandler = (e) => {
  const targetLink = e.target.closest('link');
  if (!targetLink) return;
  e.preventDefault();
  this.props.history.push(e.target.href);
};

const CurrentContest = ({ contests, showDate = false }) => {
  if (contests.length > 0) {
    const contest = contests[0];
    const title = contest.title;
    const description = contest.description;
    const startDate = new Date(contest.start_date).toDateString();
    const endDate = new Date(contest.end_date).toDateString();
    const params = new URLSearchParams(window.location.search);
    const termsLink = contest.terms_url;
    let mobile = false;

    if (params.get('mobile')) {
      mobile = true;
    }

    return (
      <div
        className="current-contest__container"
        onClick={contentClickHandler}
        role="menuitem"
        tabIndex={0}
        aria-hidden="true"
      >
        <div id="current_contest">
          <h6
            className="current-contest__title"
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
          <p
            className="current-contest__description"
            dangerouslySetInnerHTML={{
              __html: description,
            }}
          />
          {showDate && (
            <p className="current-contest__dates">
              {i18n.translate('general.from')} {startDate} {i18n.translate('general.to')} {endDate}
            </p>
          )}
          {termsLink && (
            <p className="current-contest__terms">
              {mobile && (
                <a href={termsLink} className="linkTerms" data-fancybox="terms" data-type="iframe">
                  {i18n.translate('rewards.home.contestTerms')}
                </a>
              )}
              {!mobile && (
                <a href={termsLink} className="linkTerms" data-fancybox="terms" data-type="iframe">
                  {i18n.translate('rewards.home.contestTerms')}
                </a>
              )}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="current-contest__container">
      <h6 className="current-contest__no-contest">{i18n.translate('rewards.home.noContest')}</h6>
    </div>
  );
};

/**
 * `RewardsView`
 *
 * @returns {JSX.Element}
 */
const RewardsView = () => {
  const [email, setEmail] = useState('');
  const [contests, setContests] = useState([]);
  const [featureFlags, setFeatureFlags] = useState({});
  const { user } = useUserInfo();
  const [isRewardsCountryEligible, setIsRewardsCountryEligible] = useState(false);
  useEffect(() => {
    isRewardsCountry(user?.country).then(setIsRewardsCountryEligible);
  }, [user?.country]);

  useEffect(() => {
    getFeatureFlags().then((flags) => {
      setFeatureFlags(flags);
    });
  }, []);

  const locationSearch = window.location.search;
  const params = useMemo(() => {
    return new URLSearchParams(locationSearch);
  }, [locationSearch]);

  let mobile = false;
  if (params.get('mobile')) {
    mobile = true;
  }

  useEffect(() => {
    if (contests.length === 0) {
      StorageHandler.load(storageKeys.lang).then(async (lang) => {
        const result = await APIHandler.getContests(import.meta.env.VITE_APP_ID, {
          currentDate: new Date().toISOString(),
          marketFilter: [{ id: lang.code.toUpperCase() }],
        });

        if (Array.isArray(result) && result.length > 0) {
          const token = await StorageHandler.load(storageKeys.accessToken);
          const data = await Promise.all(
            result.map(async (contest) => {
              const rewards = await APIHandler.getRewards(
                import.meta.env.VITE_APP_ID,
                token,
                contest.start_date,
                contest.end_date,
              );
              return {
                ...contest,
                entriesCount: Array.isArray(rewards.response) ? rewards.response.length : 0,
              };
            }),
          );
          setContests(data);
        }
      });
    }
  }, [contests, setContests]);

  // Track share event
  useEffect(() => {
    trackShare();
  }, []);

  useEffect(() => {
    document.body.classList.remove('no-scroll');
    StorageHandler.load(storageKeys.user).then((res) => {
      if (res) {
        setEmail((prev) => {
          return res.email !== prev ? res.email : prev;
        });
      }
    });

    if (mobile) {
      const token = params.get('token');
      // If there is no token in the url, check the validity of the token currently stored.
      if (!token) {
        const expiredStoredToken = StorageHandler.isExpired(storageKeys.accessToken);
        if (expiredStoredToken) {
          window.location.href = '/main/logout';
        }
        return;
      }

      StorageHandler.save(storageKeys.accessToken, token);
      // Temp save to prevent redirect to logout
      StorageHandler.save(storageKeys.user, { email: params.get('email') }, 2592000000);
      (async () => {
        const appId = await DataHandler.matchAppId();
        const userResponse = await APIHandler.getUser(appId, token);
        if (userResponse?.response?.status === 401 || !userResponse?.email || Object.keys(userResponse).length === 0) {
          window.location.href = '/main/logout';
          return;
        }

        StorageHandler.save(storageKeys.user, userResponse, 2592000000);
        setEmail(userResponse.email);
      })();
    } else {
      StorageHandler.load(storageKeys.accessToken).then((accessToken) => {
        if (!accessToken) {
          window.location.href = '/logout';
        }
      });
    }
  }, [email, setEmail, mobile, params]);

  let mobileLink = '';
  if (params.get('mobile')) {
    mobileLink = '?mobile=true';
  }

  return (
    <div className="contentContainer rewardsView">
      <RewardMenu />
      <div className="bodyRewards">
        <div className="holder">
          <ContentHeader
            header={i18n.translate(featureFlags.ambassador ? 'rewards.titleAmbassador' : 'rewards.title')}
            subtitle={i18n.translate(featureFlags.ambassador ? 'rewards.subheaderAmbassador' : 'rewards.subheader')}
            pageHeader
          />
          <hr />
          <ContentHeader icon={['fas', 'trophy']} header={i18n.translate('rewards.home.currentContest')} layout="row" />
          <CurrentContest contests={contests} showDate={false} />
          {isRewardsCountryEligible &&
            (featureFlags.badges || featureFlags.metallic_tiers || featureFlags.has_achievements_rewards_page) && (
              <React.Fragment>
                <hr />

                <Link to={`/main/rewards/achievements${mobileLink}`} className="linkRewards">
                  <ContentHeader
                    icon={['fas', 'bars']}
                    header={i18n.translate('rewards.home.trainingAchievements')}
                    subtitle={i18n.translate(
                      featureFlags.metallic_tiers
                        ? 'rewards.home.trainingAchievementsText'
                        : 'rewards.home.trainingAchievementsTextNoMetallic',
                    )}
                    layout="row"
                  />
                </Link>
              </React.Fragment>
            )}
          <hr />
          {featureFlags.rewards_booking && isRewardsCountryEligible && (
            <React.Fragment>
              {!featureFlags.is_booking_dashboard_disabled && (
                <React.Fragment>
                  <Link to={`/main/rewards/dashboard${mobileLink}`} className="linkRewards">
                    <ContentHeader
                      icon={['far', 'badge']}
                      header={i18n.translate('rewards.home.bookingContests')}
                      subtitle={i18n.translate('rewards.home.bookingContestsText')}
                      layout="row"
                    />
                  </Link>
                  <hr />
                </React.Fragment>
              )}

              <Link to={`/main/rewards/form${mobileLink}`} className="linkRewards">
                <ContentHeader
                  icon="upload"
                  header={i18n.translate('rewards.home.submitBooking')}
                  subtitle={i18n.translate('rewards.home.submitBookingText')}
                  layout="row"
                />
              </Link>
              <hr />
            </React.Fragment>
          )}
        </div>
      </div>
      {mobile && (
        <style>
          {`.contentContainer { margin-top: ${legacyRemCss(5)}; } .header { display: none; } .cookieConsent { display: none; }`}
          {`.thankYouView__wrapper { margin-top: ${legacyRemCss(5)} !important; }`}
        </style>
      )}
    </div>
  );
};

export default RewardsView;
